/**
 * WHICH KEYSTROKE IS THIS? (th-i5i95).
 *
 * The other half of the keyboard contract `keyboardOwnership.js` opens: that
 * module decides WHO owns a keystroke, this one decides WHICH keystroke it is.
 *
 * `vue-three-shortkey` indexes a chord by concatenating the modifier names it
 * sees with `event.key` — `['ctrl','alt','f']` registers as `ctrlaltf`, and a
 * press is looked up the same way. That identity holds only while the letter a
 * key PRODUCES matches the letter it is PRINTED with, and a glyph modifier
 * breaks exactly that. On macOS, Control + Option + F arrives as
 * `event.key === 'ƒ'`: with Control held the OS puts a control character in
 * `NSEvent.characters`, Chromium rejects it as a DOM key and regenerates the
 * key per UI Events step 4 keeping only the GLYPH modifiers — Shift, CapsLock
 * and Option — so Option's Mac Roman layer wins. S becomes 'ß', T becomes '†'.
 * The pressed index reads `ctrlaltƒ`, the registered one `ctrlaltf`, nothing
 * matches, and the three workspace-layout shortcuts never fire for any Mac.
 * AltGr on Windows/Linux is the same event shape — `ctrlKey` + `altKey` plus a
 * rewritten glyph — and fails the same way, so this is a class, not a chord.
 *
 * `event.code` is immune: it names the PHYSICAL key ('KeyF'), which is what a
 * chord that spells its modifiers out meant all along. So an Alt chord is
 * re-identified by its code and NOTHING else is: a bare letter, `/`, Escape,
 * `WeTab`'s shift-push and every non-Alt binding keep character identity on
 * every layout, because those are read off the key the viewer actually typed.
 *
 * WHY THE DECODER RATHER THAN A LISTENER. The published bundle installs its own
 * document-CAPTURE keydown/keyup pair at import time and calls `i.decodeKey(e)`
 * through the EXPORTED object on every press, so replacing `decodeKey` in place
 * changes chord identity for the real interceptor — with no second global
 * keydown listener racing it (the `useEscapeDismiss` source invariant ratchets
 * those) and no patch to the library. The decoder reads only `key` and the four
 * modifier flags, which is exactly what the view below carries.
 */

/** A physical character key: the only `code` shape a chord letter comes from. */
const PHYSICAL_CHARACTER_CODE = /^(?:Key|Digit)([A-Z0-9])$/;

/**
 * The event as a chord decoder should read it.
 *
 * @param {KeyboardEvent} event the press, exactly as the platform delivered it.
 * @returns {KeyboardEvent|{shiftKey: boolean, ctrlKey: boolean, metaKey: boolean, altKey: boolean, key: string}}
 *   the SAME event whenever character identity is the honest reading, and a
 *   plain view carrying the physical letter or digit when a glyph modifier has
 *   rewritten it.
 */
export function chordKeyView(event) {
  if (!event || !event.altKey) return event;
  const physical = PHYSICAL_CHARACTER_CODE.exec(event.code || "");
  if (!physical) return event;
  return {
    shiftKey: event.shiftKey,
    ctrlKey: event.ctrlKey,
    metaKey: event.metaKey,
    altKey: event.altKey,
    key: physical[1].toLowerCase(),
  };
}

/** The wrapper's own mark, so a second install cannot stack a second decoder. */
const PHYSICAL_KEY_IDENTITY = Symbol.for("towerHunt.physicalKeyChordIdentity");

/**
 * Give the plugin physical-key chord identity, in place and once.
 *
 * @param {{decodeKey: Function}} shortKey the `vue-three-shortkey` module object
 *   the app installs — the same object its document listeners call through.
 * @returns {{decodeKey: Function}} that object, for the caller's convenience.
 */
export function installPhysicalKeyChordIdentity(shortKey) {
  const byCharacter = shortKey.decodeKey;
  if (byCharacter[PHYSICAL_KEY_IDENTITY]) return shortKey;

  const byPhysicalKey = (event) => byCharacter(chordKeyView(event));
  byPhysicalKey[PHYSICAL_KEY_IDENTITY] = true;
  shortKey.decodeKey = byPhysicalKey;
  return shortKey;
}
